import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  workItemTemplateApi,
  WorkItemTemplateCreate,
  WorkItemTemplateUpdate,
} from "@/services/workItemTemplateApi";
import { toast } from "@/components/ui/use-toast";

export const useWorkItemTemplates = (
  departmentId: string,
  includeInactive = false
) => {
  return useQuery({
    queryKey: ["workItemTemplates", departmentId, { includeInactive }],
    queryFn: () =>
      workItemTemplateApi.getByDepartment(departmentId, includeInactive),
    enabled: !!departmentId,
  });
};

export const useCreateWorkItemTemplates = (departmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WorkItemTemplateCreate[]) =>
      workItemTemplateApi.createBulk(departmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["workItemTemplates", departmentId],
      });
      toast({
        title: "Success",
        description: "Work items created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.detail || "Failed to create work items",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateWorkItemTemplate = (departmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workItemId,
      data,
    }: {
      workItemId: string;
      data: WorkItemTemplateUpdate;
    }) => workItemTemplateApi.update(departmentId, workItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["workItemTemplates", departmentId],
      });
      toast({
        title: "Success",
        description: "Work item updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.detail || "Failed to update work item",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteWorkItemTemplate = (departmentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workItemId: string) =>
      workItemTemplateApi.delete(departmentId, workItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["workItemTemplates", departmentId],
      });
      toast({
        title: "Success",
        description: "Work item deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.detail || "Failed to delete work item",
        variant: "destructive",
      });
    },
  });
};
